import React, { useCallback, useEffect, useRef } from 'react';
import { Bold, Italic, List, ListOrdered } from 'lucide-react';

interface SwiRichTextFieldProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  /** Expande para preencher o painel (aba SWI). */
  fillHeight?: boolean;
  /** Incremento dispara focus programático no editor. */
  focusRequest?: number;
}

const btnCls =
  'rounded p-1 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200';

/** Editor rich text leve (contentEditable) para orientações passo a passo da SWI. */
export const SwiRichTextField: React.FC<SwiRichTextFieldProps> = ({
  value,
  onChange,
  placeholder = 'Descreva o passo a passo da rotina…',
  minHeight = 120,
  fillHeight = false,
  focusRequest = 0,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || el.innerHTML === value) return;
    el.innerHTML = value || '';
  }, [value]);

  useEffect(() => {
    if (!focusRequest) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (sel) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [focusRequest]);

  const sync = useCallback(() => {
    const html = ref.current?.innerHTML ?? '';
    onChange(html === '<br>' ? '' : html);
  }, [onChange]);

  const exec = (cmd: string) => {
    document.execCommand(cmd, false);
    ref.current?.focus();
    sync();
  };

  const shellCls = fillHeight
    ? 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/40'
    : 'overflow-hidden rounded-md border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/40';

  const editorCls = fillHeight
    ? 'swi-rich-text min-h-0 flex-1 overflow-y-auto px-3 py-2 text-[13px] leading-relaxed text-slate-700 outline-none dark:text-slate-200'
    : 'swi-rich-text px-3 py-2 text-[12px] leading-relaxed text-slate-700 outline-none dark:text-slate-200';

  return (
    <div className={shellCls}>
      <div className="flex shrink-0 items-center gap-0.5 border-b border-slate-100 px-1.5 py-1 dark:border-slate-800">
        <button type="button" className={btnCls} onClick={() => exec('bold')} title="Negrito">
          <Bold size={13} />
        </button>
        <button type="button" className={btnCls} onClick={() => exec('italic')} title="Itálico">
          <Italic size={13} />
        </button>
        <button type="button" className={btnCls} onClick={() => exec('insertUnorderedList')} title="Lista">
          <List size={13} />
        </button>
        <button type="button" className={btnCls} onClick={() => exec('insertOrderedList')} title="Lista numerada">
          <ListOrdered size={13} />
        </button>
      </div>
      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-multiline
        data-placeholder={placeholder}
        onInput={sync}
        onBlur={sync}
        className={editorCls}
        style={fillHeight ? undefined : { minHeight }}
      />
    </div>
  );
};

export default SwiRichTextField;
